import React, { useState, useEffect, useContext, useMemo } from "react";
import { FaUpload, FaSearch, FaCheck, FaInfoCircle } from "react-icons/fa";
import ImportLeadsModal from "./ImportLeadsModal";
import { db } from "../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { AuthContext } from "../../AuthProvider";

const LeadSelector = ({
  activeTab,
  setActiveTab,
  availableLeads,
  selectedLeads,
  onLeadSelect,
  setAvailableLeads,
  campaignId,
}) => {
  const { user } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name"); // name, company, email, location
  const [showImportModal, setShowImportModal] = useState(
    activeTab === "upload"
  );
  const [leadsPerPage, setLeadsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: "",
    company: "",
    keywords: "",
    industry: "",
    country: "",
    companySize: "",
    companyRevenue: "",
    experience: "",
  });

  // Watch for activeTab changes to control modal visibility
  useEffect(() => {
    setShowImportModal(activeTab === "upload");
  }, [activeTab]);

  // Load leads from Firestore - combines both loading and availability checking
  useEffect(() => {
    const loadLeads = async () => {
      if (!user) return;
      setLoading(true);
      setError(null);
      console.log("Loading leads from Firestore...");

      try {
        const leadsRef = collection(
          db,
          "userContent",
          user.uid,
          "aiSdr",
          "collections",
          "leads"
        );
        const leadsSnapshot = await getDocs(leadsRef);
        const leadsData = leadsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          isAvailable: true, // Mark all leads as available by default
        }));
        console.log("Loaded leads:", leadsData.length);

        // Only check for active campaigns if we have some leads
        if (leadsData.length > 0 && campaignId) {
          try {
            // If this is an edit of an existing campaign, we should check availability
            console.log("Checking lead availability for campaigns...");
            const campaignsRef = collection(
              db,
              "userContent",
              user.uid,
              "aiSdr",
              "collections",
              "campaigns"
            );

            // Just get all active campaigns
            const activeCampaignsQuery = query(
              campaignsRef,
              where("status", "==", "active")
            );

            const campaignsSnapshot = await getDocs(activeCampaignsQuery);
            console.log(
              "Found active campaigns:",
              campaignsSnapshot.docs.length
            );

            // If we have active campaigns, mark leads that are in use
            if (campaignsSnapshot.docs.length > 0) {
              const leadsInActiveCampaigns = new Set();

              campaignsSnapshot.docs.forEach((doc) => {
                const campaign = doc.data();
                // Skip the current campaign being edited
                if (doc.id !== campaignId) {
                  // Check for selectedLeads array
                  if (
                    campaign.selectedLeads &&
                    Array.isArray(campaign.selectedLeads)
                  ) {
                    campaign.selectedLeads.forEach((leadId) => {
                      leadsInActiveCampaigns.add(leadId);
                    });
                  }
                }
              });

              // Update availability flag for leads in active campaigns
              leadsData.forEach((lead) => {
                if (leadsInActiveCampaigns.has(lead.id)) {
                  lead.isAvailable = false;
                }
              });
            }
          } catch (campaignErr) {
            console.error(
              "Error checking campaigns (non-critical):",
              campaignErr
            );
            // Just continue with all leads available
          }
        }

        // Set the leads data once, with all processing done
        setAvailableLeads(leadsData);
      } catch (err) {
        console.error("Error loading leads:", err);
        setError("Failed to load leads. Please try again.");
      } finally {
        // Always set loading to false when done
        setLoading(false);
      }
    };

    loadLeads();
  }, [user, campaignId, setAvailableLeads]); // Only depend on user and campaignId

  // Filter leads based on search term and field
  const filteredLeads = useMemo(() => {
    if (!searchTerm) return availableLeads;

    const term = searchTerm.toLowerCase();
    switch (searchField) {
      case "name":
        return availableLeads.filter(
          (lead) =>
            (lead.name && lead.name.toLowerCase().includes(term)) ||
            (lead.firstName && lead.firstName.toLowerCase().includes(term)) ||
            (lead.lastName && lead.lastName.toLowerCase().includes(term))
        );
      case "company":
        return availableLeads.filter((lead) =>
          lead.company?.toLowerCase().includes(term)
        );
      case "email":
        return availableLeads.filter((lead) =>
          lead.email?.toLowerCase().includes(term)
        );
      case "location":
        return availableLeads.filter(
          (lead) =>
            lead.cityStateZip?.toLowerCase().includes(term) ||
            lead.address?.toLowerCase().includes(term)
        );
      default:
        return availableLeads;
    }
  }, [availableLeads, searchTerm, searchField]);

  // Pagination
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstLead, indexOfLastLead);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleLeadToggle = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      onLeadSelect(selectedLeads.filter((id) => id !== leadId));
    } else {
      // Check if we've reached the lead limit
      if (selectedLeads.length >= 100) {
        setError("Maximum of 100 leads can be added to a campaign");
        return;
      }
      onLeadSelect([...selectedLeads, leadId]);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectAll = () => {
    if (
      filteredLeads.length > 0 &&
      selectedLeads.length === filteredLeads.length
    ) {
      onLeadSelect([]);
    } else {
      const newSelectedLeads = [
        ...new Set([...selectedLeads, ...filteredLeads.map((lead) => lead.id)]),
      ];
      if (newSelectedLeads.length > 100) {
        setError("Maximum of 100 leads can be added to a campaign");
        return;
      }
      onLeadSelect(newSelectedLeads);
    }
  };

  const handleImportLeads = (importedLeads) => {
    if (importedLeads.length > 0) {
      setAvailableLeads((prev) => [...prev, ...importedLeads]);
      onLeadSelect([...selectedLeads, ...importedLeads.map((lead) => lead.id)]);
    }
    setShowImportModal(false);
    setActiveTab("existing");
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setActiveTab("existing");
  };

  return (
    <div className="space-y-4">
      <div className="flex space-x-4 border-b">
        <button
          className={`px-4 py-2 ${
            activeTab === "existing"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("existing")}
        >
          Select from existing
        </button>
        <button
          className={`px-4 py-2 ${
            activeTab === "upload"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("upload")}
        >
          Upload new leads
        </button>
        <button
          className={`px-4 py-2 ${
            activeTab === "find"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("find")}
        >
          Find leads
        </button>
      </div>

      {activeTab === "existing" && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="Search leads..."
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-2"></div>
              <p>Loading leads...</p>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-4 bg-red-50 rounded p-3">
              <p className="font-medium">Error:</p>
              <p>{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-2 px-4 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
              >
                Try Again
              </button>
            </div>
          ) : availableLeads.length === 0 ? (
            <div className="text-gray-500 text-center py-8 bg-gray-50 rounded p-6">
              <p className="text-lg mb-2">No leads found</p>
              <p>
                Try importing leads by selecting the "Upload new leads" tab
                above.
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={
                          filteredLeads.length > 0 &&
                          filteredLeads.every((lead) =>
                            selectedLeads.includes(lead.id)
                          )
                        }
                        onChange={() => {
                          if (
                            filteredLeads.length > 0 &&
                            filteredLeads.every((lead) =>
                              selectedLeads.includes(lead.id)
                            )
                          ) {
                            onLeadSelect(
                              selectedLeads.filter(
                                (id) =>
                                  !filteredLeads.some((lead) => lead.id === id)
                              )
                            );
                          } else {
                            const newLeads = [
                              ...selectedLeads,
                              ...filteredLeads
                                .filter(
                                  (lead) => !selectedLeads.includes(lead.id)
                                )
                                .map((lead) => lead.id),
                            ];
                            if (newLeads.length > 100) {
                              setError(
                                "Maximum of 100 leads can be added to a campaign"
                              );
                              return;
                            }
                            onLeadSelect(newLeads);
                          }
                        }}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentLeads.map((lead) => (
                    <tr key={lead.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => {
                            if (selectedLeads.includes(lead.id)) {
                              onLeadSelect(
                                selectedLeads.filter((id) => id !== lead.id)
                              );
                            } else {
                              if (selectedLeads.length >= 100) {
                                setError(
                                  "Maximum of 100 leads can be added to a campaign"
                                );
                                return;
                              }
                              onLeadSelect([...selectedLeads, lead.id]);
                            }
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {lead.name ||
                            (lead.firstName && lead.lastName
                              ? `${lead.firstName} ${lead.lastName}`
                              : lead.firstName || lead.lastName || "Unnamed")}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {lead.company || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {lead.email || "N/A"}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Showing {indexOfFirstLead + 1} to{" "}
                {Math.min(indexOfLastLead, filteredLeads.length)} of{" "}
                {filteredLeads.length} leads
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                  }`}
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => paginate(i + 1)}
                    className={`px-3 py-1 rounded ${
                      currentPage === i + 1
                        ? "bg-purple-600 text-white"
                        : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    paginate(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "find" && (
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-1/4">
            {/* Filters Panel */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-700">
                  Advanced Search
                </h3>
                <button
                  onClick={() =>
                    setFilters({
                      name: "",
                      company: "",
                      keywords: "",
                      industry: "",
                      country: "",
                      companySize: "",
                      companyRevenue: "",
                      experience: "",
                    })
                  }
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear filters
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={filters.name}
                    onChange={handleFilterChange}
                    placeholder="Search by name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={filters.company}
                    onChange={handleFilterChange}
                    placeholder="Search by company"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Keywords
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="keywords"
                      value={filters.keywords}
                      onChange={handleFilterChange}
                      placeholder="Enter keywords"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div
                      className="absolute right-2 top-2 text-gray-400 cursor-help"
                      title="Search for keywords in job titles, descriptions, or skills"
                    >
                      <FaInfoCircle />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry
                  </label>
                  <select
                    name="industry"
                    value={filters.industry}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Any Industry</option>
                    <option value="Software">Software</option>
                    <option value="Finance">Finance</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Education">Education</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Retail">Retail</option>
                    <option value="Agriculture">Agriculture</option>
                    <option value="Energy">Energy</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={filters.country}
                    onChange={handleFilterChange}
                    placeholder="Search for a lead country"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company size
                  </label>
                  <select
                    name="companySize"
                    value={filters.companySize}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Any Size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1001+">1001+ employees</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company revenue
                  </label>
                  <select
                    name="companyRevenue"
                    value={filters.companyRevenue}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Any Revenue</option>
                    <option value="<1M">Less than $1M</option>
                    <option value="1M-10M">$1M - $10M</option>
                    <option value="10M-50M">$10M - $50M</option>
                    <option value="50M-100M">$50M - $100M</option>
                    <option value="100M-1B">$100M - $1B</option>
                    <option value="1B+">$1B+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Experience
                  </label>
                  <select
                    name="experience"
                    value={filters.experience}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Any Experience</option>
                    <option value="0-2">0-2 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="11-15">11-15 years</option>
                    <option value="15+">15+ years</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="md:w-3/4">
            {/* Results Table */}
            <div className="bg-white rounded-lg border">
              <div className="p-4 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-700">Leads</h3>
                  <div className="text-sm text-gray-500">
                    {selectedLeads.length} of {filteredLeads.length} leads
                    selected
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={
                            filteredLeads.length > 0 &&
                            filteredLeads.every((lead) =>
                              selectedLeads.includes(lead.id)
                            )
                          }
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentLeads.length > 0 ? (
                      currentLeads.map((lead) => (
                        <tr
                          key={lead.id}
                          className={`${
                            selectedLeads.includes(lead.id)
                              ? "bg-blue-50"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedLeads.includes(lead.id)}
                              onChange={() => handleLeadToggle(lead.id)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.name ||
                                (lead.firstName && lead.lastName
                                  ? `${lead.firstName} ${lead.lastName}`
                                  : lead.firstName ||
                                    lead.lastName ||
                                    "Unnamed")}
                            </div>
                            <div className="text-sm text-gray-500">
                              {lead.email || "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {lead.role || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {lead.company || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {lead.cityStateZip || "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-6 py-4 text-center text-gray-500"
                        >
                          No leads found matching your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination for Find tab */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      Showing {indexOfFirstLead + 1} to{" "}
                      {Math.min(indexOfLastLead, filteredLeads.length)} of{" "}
                      {filteredLeads.length} leads
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => paginate(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded ${
                          currentPage === 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                        }`}
                      >
                        Previous
                      </button>
                      {[...Array(totalPages)].map((_, i) => (
                        <button
                          key={i}
                          onClick={() => paginate(i + 1)}
                          className={`px-3 py-1 rounded ${
                            currentPage === i + 1
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() =>
                          paginate(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded ${
                          currentPage === totalPages
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <ImportLeadsModal
          onClose={handleCloseImportModal}
          onImport={handleImportLeads}
        />
      )}
    </div>
  );
};

export default LeadSelector;
